import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../../contexts/TenantContext';

const TARGET_GROUPS = [
  'Ärzte',
  'Zahnärzte',
  'Physiotherapeuten',
  'Apotheken',
  'Pflegedienste',
  'Heilpraktiker',
  'Psychotherapeuten',
  'Ergotherapeuten',
  'Logopäden',
  'Podologen'
];

const PRODUCT_CATEGORIES = [
  'Nahrungsergänzungsmittel',
  'Medizinprodukte',
  'Kosmetik',
  'Wellness',
  'Diagnostik',
  'Therapie',
  'Praxisbedarf',
  'Software',
  'Fortbildung',
  'Marketing'
];

export default function SetupPage() {
  const navigate = useNavigate();
  const { tenant, tenantSettings, updateTenantSettings } = useTenant();
  
  const [targetGroups, setTargetGroups] = useState<string[]>([]);
  const [customGroup, setCustomGroup] = useState('');
  const [city, setCity] = useState('');
  const [radius, setRadius] = useState(50);
  const [productCategories, setProductCategories] = useState<string[]>([]);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (tenantSettings) {
      setTargetGroups(tenantSettings.target_groups || []);
      setProductCategories(tenantSettings.product_categories || []);
      setEmailEnabled(tenantSettings.email_enabled || false);
      
      // Regions aus Settings laden (Format: ["Stadt:Radius"])
      if (tenantSettings.regions && tenantSettings.regions.length > 0) {
        const [savedCity, savedRadius] = tenantSettings.regions[0].split(':');
        setCity(savedCity || '');
        setRadius(parseInt(savedRadius) || 50);
      }
    }
  }, [tenantSettings]);

  const toggleTargetGroup = (group: string) => {
    setTargetGroups(prev =>
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const addCustomGroup = () => {
    if (customGroup.trim() && !targetGroups.includes(customGroup.trim())) {
      setTargetGroups(prev => [...prev, customGroup.trim()]);
      setCustomGroup('');
    }
  };

  const toggleProductCategory = (category: string) => {
    setProductCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSave = async () => {
    setError('');
    
    if (targetGroups.length === 0) {
      setError('Bitte wählen Sie mindestens eine Zielgruppe aus');
      return;
    }

    if (!city.trim()) {
      setError('Bitte geben Sie eine Stadt ein');
      return;
    }

    if (productCategories.length === 0) {
      setError('Bitte wählen Sie mindestens eine Produktkategorie aus');
      return;
    }

    setLoading(true);

    try {
      await updateTenantSettings({
        target_groups: targetGroups,
        regions: [`${city}:${radius}`],
        product_categories: productCategories,
        email_enabled: emailEnabled
      });

      // Zur Dashboard weiterleiten
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Save error:', err);
      setError('Einstellungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Willkommen bei LeadGen</h1>
              <p className="text-slate-600 mt-1">Richten Sie Ihr Konto ein, um zu starten</p>
            </div>
            <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
              <i className="ri-settings-3-line text-white text-2xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Zielgruppen */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-group-line text-teal-500"></i>
              Zielgruppen
            </h2>
            <p className="text-slate-600 mb-4">Wählen Sie die Branchen aus, die Sie ansprechen möchten</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {TARGET_GROUPS.map(group => (
                <button
                  key={group}
                  onClick={() => toggleTargetGroup(group)}
                  className={`px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm whitespace-nowrap cursor-pointer ${
                    targetGroups.includes(group)
                      ? 'bg-teal-50 border-teal-500 text-teal-700'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {group}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={customGroup}
                onChange={(e) => setCustomGroup(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomGroup()}
                placeholder="Eigene Zielgruppe hinzufügen"
                className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all outline-none text-sm"
              />
              <button
                onClick={addCustomGroup}
                className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all font-medium whitespace-nowrap cursor-pointer"
              >
                Hinzufügen
              </button>
            </div>
          </div>

          {/* Region */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-map-pin-line text-teal-500"></i>
              Region
            </h2>
            <p className="text-slate-600 mb-4">Definieren Sie Ihr Suchgebiet</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Stadt
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="z.B. Berlin"
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Radius (km): {radius}
                </label>
                <input
                  type="range"
                  min="10"
                  max="200"
                  step="10"
                  value={radius}
                  onChange={(e) => setRadius(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
                />
              </div>
            </div>
          </div>

          {/* Produktkategorien */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-shopping-bag-line text-teal-500"></i>
              Produktkategorien
            </h2>
            <p className="text-slate-600 mb-4">Welche Produkte bieten Sie an?</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {PRODUCT_CATEGORIES.map(category => (
                <button
                  key={category}
                  onClick={() => toggleProductCategory(category)}
                  className={`px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm whitespace-nowrap cursor-pointer ${
                    productCategories.includes(category)
                      ? 'bg-teal-50 border-teal-500 text-teal-700'
                      : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* E-Mail Feature */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-mail-line text-teal-500"></i>
              E-Mail-Funktion
            </h2>
            <p className="text-slate-600 mb-4">Optional: E-Mail-Entwürfe für Leads erstellen</p>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
                className="w-5 h-5 text-teal-500 border-slate-300 rounded focus:ring-teal-500 cursor-pointer"
              />
              <span className="text-slate-700 font-medium">E-Mail-Entwürfe aktivieren</span>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={loading}
            className="w-full bg-teal-500 text-white font-semibold py-4 rounded-lg hover:bg-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-xl"></i>
                <span>Wird gespeichert...</span>
              </>
            ) : (
              <>
                <i className="ri-check-line text-xl"></i>
                <span>Einstellungen speichern</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
